import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import {
  HIDDEN_KEYS,
  filterVisible,
  hideItem,
} from "../utils/hiddenHistory";
import { formatAppDateTime } from "../utils/dateFormat";
import { computeCustomerDisplay } from "../utils/customerBalance";

export default function Customer() {
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [listVersion, setListVersion] = useState(0);
  const [showDrCr, setShowDrCr] = useState(false);
  const [customer, setCustomer] = useState({
    name: "",
    place: "",
    opening_balance: 0,
  });
  const [payment, setPayment] = useState({
    type: "credit",
    amount: 0,
    customer_id: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loading, setLoading] = useState(true);
  const [addCustomerNameError, setAddCustomerNameError] = useState("");
  const [showCustomerTransactions, setShowCustomerTransactions] =
    useState(false);
  const [transactionCustomer, setTransactionCustomer] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const normalizeCustomerName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const [customersRes, salesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/customers`),
        axios.get(`${API_BASE_URL}/api/sales`),
      ]);
      setCustomers(
        Array.isArray(customersRes.data) ? customersRes.data : [],
      );
      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
    } catch (error) {
      console.error("Error fetching customers", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      setAddCustomerNameError("");
    }
    setCustomer({
      ...customer,
      [name]: name === "opening_balance" ? Number(value) : value,
    });
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPayment({
      ...payment,
      [name]: name === "amount" ? Number(value) : value,
    });
  };

  const formatNumber = (value) => Number(value ?? 0).toFixed(2);

  const openCustomerTransactions = async (cust) => {
    setTransactionCustomer(cust);
    setShowCustomerTransactions(true);
    setLoadingTransactions(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/sales`);
      const sales = Array.isArray(response.data) ? response.data : [];
      setCustomerTransactions(
        filterVisible(
          sales.filter((sale) => String(sale.customer_id) === String(cust.id)),
          HIDDEN_KEYS.sales,
        ),
      );
    } catch (error) {
      console.error("Error loading customer transactions", error);
      setCustomerTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const deleteSale = (saleId) => {
    if (
      !window.confirm(
        "Remove this transaction from the list? Restore it from the History page.",
      )
    ) {
      return;
    }

    hideItem(HIDDEN_KEYS.sales, saleId);
    setCustomerTransactions((prev) =>
      prev.filter((item) => String(item.id) !== String(saleId)),
    );
    setListVersion((v) => v + 1);
    alert("Transaction moved to History. Balance updated on screen.");
  };

  const savePayment = async (e) => {
    e.preventDefault();
    if (!payment.customer_id) {
      alert("Select a customer for the transaction");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/customers/${payment.customer_id}/payment`,
        payment,
      );
      alert("Customer transaction recorded");
      setPayment({ type: "credit", amount: 0, customer_id: "" });
      setShowDrCr(false);
      fetchCustomers();
    } catch (error) {
      console.error("Error recording payment", error);
      alert("Failed to record customer transaction");
    }
  };

  const displayedCustomers = useMemo(() => {
    void listVersion;
    return customers.map((cust) => {
      const totals = computeCustomerDisplay(cust, sales);
      return { ...cust, ...totals };
    });
  }, [customers, sales, listVersion]);

  const filteredCustomers = selectedCustomer
    ? displayedCustomers.filter(
        (cust) => String(cust.id) === String(selectedCustomer),
      )
    : displayedCustomers;

  const totalBalance = filteredCustomers.reduce(
    (sum, cust) => sum + Number(cust.balance ?? 0),
    0,
  );

  const totalDr = filteredCustomers.reduce(
    (sum, cust) => sum + Number(cust.debit ?? 0),
    0,
  );

  const totalCr = filteredCustomers.reduce(
    (sum, cust) => sum + Number(cust.credit ?? 0),
    0,
  );

  if (loading) {
    return <Loader message="Loading customers…" fullPage />;
  }

  return (
    <>
      <div className="container-fluid app-container">
        <div className="row mb-3">
          <div className="col-12 app-action-bar d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
            <button
              type="button"
              className="btn btn-secondary app-btn"
              onClick={() => setShowDrCr(true)}
            >
              Customer DR / CR
            </button>
            <div className="ms-md-auto w-100 w-md-auto">
              <label
                className="form-label visually-hidden"
                htmlFor="customer-filter"
              >
                Filter customers
              </label>
              <select
                id="customer-filter"
                className="form-select"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">All customers</option>
                {displayedCustomers.length === 0 ? (
                  <option value="" disabled>
                    No customers available
                  </option>
                ) : (
                  displayedCustomers.map((cust) => (
                    <option key={cust.id} value={cust.id}>
                      {cust.name}
                      {cust.place ? ` (${cust.place})` : ""}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="card app-screen-card">
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3">
                <h4 className="app-card-title mb-2 mb-md-0">
                  Customer Balances
                </h4>
                <div className="app-sales-stats small">
                  <strong>Entries:</strong> {filteredCustomers.length}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>DR:</strong> {totalDr.toFixed(2)}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>CR:</strong> {totalCr.toFixed(2)}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>Balance:</strong> {totalBalance.toFixed(2)}
                </div>
              </div>
              <div className="table-responsive app-table-wrap">
                <table className="table table-sm app-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Place</th>
                      <th>DR</th>
                      <th>CR</th>
                      <th>Balance</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((cust) => (
                      <tr key={cust.id}>
                        <td data-label="Name">{cust.name}</td>
                        <td data-label="Place">
                          {cust.place ?? cust.type ?? "-"}
                        </td>
                        <td data-label="DR">{Number(cust.debit).toFixed(2)}</td>
                        <td data-label="CR">
                          {Number(cust.credit).toFixed(2)}
                        </td>
                        <td data-label="Balance" className="cell-highlight">
                          {Number(cust.balance).toFixed(2)}
                        </td>
                        <td data-label="Action">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openCustomerTransactions(cust)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCustomerTransactions && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCustomerTransactions(false);
              setCustomerTransactions([]);
            }
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-customer-transactions-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4
                className="app-modal__title"
                id="modal-customer-transactions-title"
              >
                {transactionCustomer?.name || "Customer"} Transactions
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={() => setShowCustomerTransactions(false)}
              >
                ×
              </button>
            </div>
            <div className="app-modal__body">
              <div className="mb-3">
                <strong>Place:</strong> {transactionCustomer?.place ?? "-"}
              </div>
              <div className="mb-3">
                <strong>Total sales:</strong> {customerTransactions.length}
              </div>
              {loadingTransactions ? (
                <p>Loading transactions…</p>
              ) : customerTransactions.length === 0 ? (
                <p>No transactions found for this customer.</p>
              ) : (
                <div className="table-responsive app-table-wrap">
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerTransactions.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDateTime(item.sale_date)}
                          </td>
                          <td data-label="Item">{item.stock?.name || "-"}</td>
                          <td data-label="Qty">{item.quantity}</td>
                          <td data-label="Unit Price">
                            {formatNumber(item.unit_price)}
                          </td>
                          <td data-label="Total" className="cell-highlight">
                            {formatNumber(item.total_amount)}
                          </td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteSale(item.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDrCr && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDrCr(false);
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-drcr-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="modal-drcr-title">
                Customer DR / CR
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={() => setShowDrCr(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={savePayment}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Customer</label>
                  <select
                    className="form-select"
                    name="customer_id"
                    value={payment.customer_id}
                    onChange={handlePaymentChange}
                    required
                  >
                    <option value="">Select customer</option>
                    {displayedCustomers.map((cust) => (
                      <option key={cust.id} value={cust.id}>
                        {cust.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Transaction Type</label>
                  <select
                    className="form-select"
                    name="type"
                    value={payment.type}
                    onChange={handlePaymentChange}
                  >
                    <option value="credit">Credit (Payment)</option>
                    <option value="debit">Debit (Sale / Charge)</option>
                  </select>
                </div>
                <div className="mb-0">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    name="amount"
                    value={payment.amount}
                    onChange={handlePaymentChange}
                    min="0.01"
                    required
                  />
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDrCr(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Record Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
